/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import Wait from '@react/react-spectrum/Wait';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import Banner from '@react/react-spectrum/Banner';
import AppHomeAnalytics from '../../../utils/AppHomeAnalytics';
import CCProjectsClientService from '../../services/CCProjectsClientService';
import Dialog from '@react/react-spectrum/Dialog';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import './AppHomeCard.scss';

type Props = {
	key: string;
	text: string;
	link: string;
	imgUrl: string;
	type: string;
	title: string;
	loadDelay: number;
	comingSoon?: boolean;
	spinner: boolean;
	cardData?: any;
	apphomeData?: any;
	context?: string;
	intl: any;
};

export const AppHomeCard = (props: Props) => {
	const [isLoading, setLoading] = useState(false);
	const ccProjectsLoaded = useSelector(state => state.appHome.ccProjectsFetchFinished);
	const appHomeAnalytics = new AppHomeAnalytics();

	useEffect(() => {
		if (props.cardData && props.context) {
			appHomeAnalytics.postAppHomeCardRender(props.cardData.id, props.cardData.title.long, props.type, props.context, true, '');
		}
	}, []);

	const initiateCanvasTemplateCopy = async() => {
		setLoading(true);
		const ccProjectsClientService = await new CCProjectsClientService();

		// Check to see if user is entitled to canvas / creating templates
		const canvasEntitled = await ccProjectsClientService.userHasCanvasEntitlement();
		if (!canvasEntitled) {
			const errorTitle = props.intl.formatMessage({id: 'appHomes.error.modal.canvas.capability.title'});
			const errorDesc = props.intl.formatMessage({id: 'appHomes.error.modal.canvas.capability.description'});
			const errorModal = (
				<Dialog confirmDisabled={false} confirmLabel={props.intl.formatMessage({id: 'apphomes.modal.ok'})} open role='dialog' title={errorTitle} trapFocus variant='error' id='canvas-error-dialog'>
					<span>{errorDesc}</span>
				</Dialog>
			);
			ModalContainer.show(errorModal, this);
			setLoading(false);
		}
		else {
			try {
				await ccProjectsClientService.createTemplateCopy(props.cardData.id, props.cardData.title.long, props.intl.formatMessage({id: 'appHomes.files.newCanvas.newSpace'}));
				setLoading(false);
			}
			catch (e) {
				const errorTitle = props.intl.formatMessage({id: 'appHomes.error.modal.canvas.title'});
				const errorDesc = props.intl.formatMessage({id: 'appHomes.error.modal.canvas.description'});
				const errorModal = (
					<Dialog confirmDisabled={false} confirmLabel={props.intl.formatMessage({id: 'apphomes.modal.ok'})} open role='dialog' title={errorTitle} trapFocus variant='error' id='canvas-error-dialog'>
						<span>{errorDesc}</span>
					</Dialog>
				);
				ModalContainer.show(errorModal, this);
				setLoading(false);
			}
		}
	};

	const handleDelay = async(e) => {
		if (props.comingSoon) {
			e.preventDefault();
			return false;
		}
		if (props.cardData && props.context) {
			appHomeAnalytics.postAppHomeCardClick(props.cardData.id, props.cardData.title.long, props.type, props.context, true, '');
		}

		if (props.cardData && props.cardData.id && props.cardData.title && props.cardData.title.long && props.cardData.contentType === 'fredStarterTemplate' && window.cchome.util.hasFeature('canvas_templates')) {
			initiateCanvasTemplateCopy();
		}
		else {
			e.preventDefault();
			setLoading(true);
			await new Promise(res => setTimeout(res, props.spinner ? props.loadDelay : 0));
			window.location.href = props.link;
		}
	};

	const handleOnKeyDown = (e) => {
		if (e.key === ' ' || e.key === 'Enter' || e.which === 13 || e.which === 32) {
			e.currentTarget.click();
		}
	};

	const getCardStyle = () =>
		// AppHome.tsx will start intializing ccProjects on render, we use a selector to see if ccProjects has already been initialized
		({
			opacity: (!ccProjectsLoaded && !props.comingSoon) ? '0.5' : '1', // we want to darken the card if ccprojects is loading
			cursor: (ccProjectsLoaded && !props.comingSoon) ? 'pointer' : 'default', // we don't want cursor event for coming soon/disabled cards
		});


	return (
		<div
			className='apphome-card'
			onClick={handleDelay}
			onKeyDown={handleOnKeyDown}
			tabIndex={0}
			role='link'
			aria-label={props.text}
			style={getCardStyle()}
		>
			<p className='apphome-card-text'>
				<span className='apphome-card-faded-text'>{props.title}</span>
				<span className='apphome-card-title'>{props.text}</span>
			</p>
			<div className='apphome-card-img-container' tabIndex={-1}>
				<img className='apphome-card-img' tabIndex={-1} src={props.imgUrl} alt={props.text} />
			</div>
			{props.spinner && isLoading && (
				<div className='apphome-card-loading'>
					<Wait size='M' centered variant='overBackground' />
				</div>
			)}
			{props.comingSoon && (
				<div className='apphome-card-banner-container'>
					<Banner
						className='apphome-card-banner'
						corner
						content={props.intl.formatMessage({id: 'app.apphomes.soon'})}
					/>
				</div>
			)}
		</div>
	);
};

export default AppHomeCard;
